import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Switch, Route, Redirect, useHistory } from 'react-router-dom';
import Sidebar from "../Sidebar/Sidebar";
import profileActions from '../../store/profileStore/profileActions';
import ScrollToTop from "../ScrollToTop";
import { getAllowedRoutes } from "../../tools/utils";
import PrivateRoutesConfig from "../../config/PrivateRoutesConfig";
import { useCurrentBranch } from "../../hooks/useCurrentBranch";
import { COOKIES, removeToken } from '../../helpers';
import styles from './Layout.module.css';
import directoryActions from "../../store/directoryStore/directoryActions";
import systemNotificationsActions from '../../store/systemNotificationsStore/systemNotificationsActions';
import SystemNotificationModal from '../SystemNotificationModal';

export default function Layout() {
    const dispatch = useDispatch();
    const history = useHistory();
    const { currentBranch, hasActiveSubscription } = useCurrentBranch();
    const branchInfo = useSelector(state => state.branchReducer.info);
    const branchPending = useSelector(state => state.branchReducer.pending);
    const branchError = useSelector(state => state.branchReducer.error);
    const profileInfo = useSelector(state => state.profileReducer.info);
    const profilePending = useSelector(state => state.profileReducer.pending);
    const profileError = useSelector(state => state.profileReducer.error);

    const isProfileLoading = profilePending || !profileInfo;
    // Wait for branch data to load - must have branchInfo with subscription data
    const isWaitingForBranch = branchPending || (!branchInfo) || (!branchInfo.subscription);
    const shouldShowBranchError = branchError && !branchInfo;

    let allowedRoutes = [];
    if (currentBranch) {
        allowedRoutes = getAllowedRoutes(PrivateRoutesConfig, [currentBranch.role]);
    }

    useEffect(() => {
        dispatch(profileActions.getProfile());
        dispatch(directoryActions.getCities());
    }, [dispatch]);

    useEffect(() => {
        if (branchInfo?.id) {
            dispatch(systemNotificationsActions.getActiveCampaigns(branchInfo.id));
        }
    }, [branchInfo?.id]);

    useEffect(() => {
        if (currentBranch && allowedRoutes.length && !allowedRoutes.map(a => a.path).includes('/')) {
            history.push(allowedRoutes[0].path);
        }
    }, [currentBranch]); // intentionally omit allowedRoutes to avoid rerender loop

    useEffect(() => {
        const allowedPaths = ['/offers'];
        const isAllowedPath = allowedPaths.some(path => window.location.pathname.startsWith(path));
        // Only redirect after both profile and branch data are loaded
        if (!isProfileLoading && !isWaitingForBranch && !hasActiveSubscription && !isAllowedPath) {
            // Always redirect to /offers for inactive subscriptions
            history.replace('/offers');
        }
    }, [hasActiveSubscription, isProfileLoading, isWaitingForBranch, history]);

    if (isProfileLoading) {
        // Profile fetch failed (e.g. stale/invalid token) — clear cookie and redirect to login
        if (profileError && !profilePending) {
            removeToken();
            return <Redirect to="/auth" />;
        }
        return (
            <div className={styles.container}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <div>Loading profile...</div>
                </div>
            </div>
        );
    }

    const renderContent = () => {
        if (isWaitingForBranch) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <div>Loading branch data...</div>
                </div>
            );
        }

        if (shouldShowBranchError) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <div>Unable to load branch data. Please pick a branch again.</div>
                </div>
            );
        }

        // Block access if subscription is not active (except for /offers page)
        const allowedPaths = ['/offers'];
        const isAllowedPath = allowedPaths.some(path => window.location.pathname.startsWith(path));
        if (!hasActiveSubscription && !isAllowedPath) {
            return (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    padding: '40px',
                    textAlign: 'center'
                }}>
                    <div style={{
                        maxWidth: '500px',
                        background: '#fff8e1',
                        padding: '30px',
                        borderRadius: '8px',
                        border: '2px solid #ffd54f'
                    }}>
                        <h2 style={{ color: '#1D1F23', marginBottom: '20px' }}>
                            Доступ ограничен
                        </h2>
                        <p style={{
                            fontSize: '16px',
                            color: '#1D1F23',
                            lineHeight: '1.6',
                            marginBottom: '10px'
                        }}>
                            Для активации программы лояльности свяжитесь с отделом продаж
                        </p>
                        <p style={{
                            fontSize: '18px',
                            fontWeight: 'bold',
                            color: '#4EB78B',
                            marginTop: '20px'
                        }}>
                            +7 708 485 71 01
                        </p>
                    </div>
                </div>
            );
        }

        // Add /offers route for all users regardless of role when subscription is inactive
        const offersRoute = PrivateRoutesConfig.find(r => r.path === '/offers');
        const specialRoutes = [];
        if (!hasActiveSubscription) {
            if (offersRoute && !allowedRoutes.find(r => r.path === '/offers')) {
                specialRoutes.push(offersRoute);
            }
        }
        const routesToRender = [...specialRoutes, ...allowedRoutes];

        return (
            <Switch>
                {routesToRender.map((route) => {
                    const {
                        path,
                        component: Component,
                        children,
                        title,
                        permission,
                        exact,
                        ...rest
                    } = route;
                    return (
                        <Route
                            {...rest}
                            key={path}
                            path={path}
                            exact={exact}
                            render={() => <Component />}
                        />
                    );
                })}
                <Redirect to="/404" />
            </Switch>
        );
    };

    return (
        <div className={styles.container}>
            <Sidebar />
            <ScrollToTop>
                <div className={styles.content}>
                    {renderContent()}
                </div>
            </ScrollToTop>
            <SystemNotificationModal />
        </div>
    );
}
